import {
  ContextMenu as TextbitContextMenu,
  useContextMenuHints,
  useTextbit
} from '@ttab/textbit'
import { ContextMenuItem } from './ContextMenuItem'
import { ContextMenuGroup } from './ContextMenuGroup'
import { cn } from '@ttab/elephant-ui/utils'
import { useEffect, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnSuggestions } from '@/hooks/useOnSuggestions'

type Suggestion = {
  text: string
  description?: string
}

export const ContextMenu = ({ className, onAccept, onRemoveAccepted }: {
  className?: string
  /**
   * Accept the marked word or phrase in this document, so it reads as settled
   * rather than as a correction. Absent where a document cannot carry the
   * decision.
   */
  onAccept?: (text: string) => void
  /** Take that acceptance back. */
  onRemoveAccepted?: (text: string) => void
}): JSX.Element => {
  const { spelling } = useContextMenuHints()
  const { lang } = useTextbit()
  const { t } = useTranslation()
  const onSuggestions = useOnSuggestions(lang)
  const onSuggestionsRef = useRef(onSuggestions)
  onSuggestionsRef.current = onSuggestions
  const [suggestions, setSuggestions] = useState<Suggestion[] | undefined>(undefined)

  const targetText = spelling?.text

  useEffect(() => {
    if (!targetText) {
      setSuggestions(undefined)
      return
    }

    let cancelled = false
    setSuggestions(undefined)

    void (async () => {
      const result = await onSuggestionsRef.current(targetText)

      if (!cancelled) {
        setSuggestions(result)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [targetText])

  if (!spelling) {
    return <></>
  }

  return (
    <TextbitContextMenu.Root className={cn(`
      group
      flex
      flex-col
      border
      rounded-lg
      divide-y
      shadow-xl
      bg-white
      border-gray-100
      dark:text-white
      dark:bg-slate-900
      dark:border-slate-800
      dark:divide-slate-800
      dark:shadow-none
      z-50
      `, className)}
    >
      <ContextMenuGroup>
        <>
          {suggestions === undefined
            && <ContextMenuItem apply={() => { }}>{t('editor:spelling.loading')}</ContextMenuItem>}
        </>
        <>
          {suggestions?.length === 0
            && <ContextMenuItem apply={() => { }}>{t('editor:spelling.noSuggestions')}</ContextMenuItem>}
        </>
        <>
          {suggestions?.map((suggestion) => (
            <ContextMenuItem key={suggestion.text} apply={() => { spelling.apply(suggestion.text) }}>
              {suggestion.text}
              {!!suggestion.description
                && <span className='em text-muted-foreground text-xs max-w-60 pt-1'>{suggestion.description}</span>}
            </ContextMenuItem>
          ))}
        </>
      </ContextMenuGroup>

      {!!spelling.text && (spelling.accepted ? !!onRemoveAccepted : !!onAccept) && (
        <ContextMenuGroup>
          <ContextMenuItem
            apply={() => {
              if (spelling.accepted) {
                onRemoveAccepted?.(spelling.text)
              } else {
                onAccept?.(spelling.text)
              }
            }}
          >
            {t(spelling.accepted ? 'editor:spelling.removeAccepted' : 'editor:spelling.accept')}
            <span className='em text-muted-foreground text-xs max-w-60 pt-1'>
              {t(spelling.accepted ? 'editor:spelling.removeAcceptedHint' : 'editor:spelling.acceptHint')}
            </span>
          </ContextMenuItem>
        </ContextMenuGroup>
      )}
    </TextbitContextMenu.Root>
  )
}
